import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingNav from "@/components/MarketingNav";
import { Brand } from "@/components/Brand";
import { publicApi } from "@/lib/api";
import {
  ArrowRight, Users2, Workflow, ShieldCheck,
  FileSignature, LayoutDashboard, Zap, CheckCircle2, Building, Briefcase, UserCheck, Boxes,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/912253/pexels-photo-912253.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TEAM_IMG = "https://images.pexels.com/photos/1181738/pexels-photo-1181738.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, type: "spring", stiffness: 120, damping: 18 } }) };

const INDUSTRIES = [
  { icon: Building, name: "Property", desc: "Landlord & property compliance, agreements, go-live gate." },
  { icon: UserCheck, name: "Recruitment / HR", desc: "Right-to-work, contracts, equipment & first-day setup." },
  { icon: Boxes, name: "Vendor / Supplier", desc: "Due-diligence, insurance, tax forms & approvals." },
  { icon: Briefcase, name: "Professional Services", desc: "Client intake, SOW signing & project kickoff." },
];

const FEATURES = [
  { icon: Workflow, title: "No-code flow builder", desc: "Drag stages and tasks into any onboarding journey your business needs." },
  { icon: FileSignature, title: "Docs & e-signatures", desc: "Collect uploads, form fields and legally-binding signatures in one place." },
  { icon: ShieldCheck, title: "Hard-stop gate", desc: "Nothing goes live until every mandatory task is verified. No exceptions." },
  { icon: LayoutDashboard, title: "Client portal", desc: "A white-glove branded portal your clients complete at their own pace." },
  { icon: Users2, title: "Team & roles", desc: "Invite your team, review submissions and keep everyone accountable." },
  { icon: Zap, title: "Live progress", desc: "Real-time completion rings and awaiting-review counts across every client." },
];

export default function Landing() {
  const [cms, setCms] = useState({});
  useEffect(() => { publicApi.get("/platform/public-settings").then((r) => setCms(r.data.landing || {})).catch(() => {}); }, []);
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav active="home" />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-lines opacity-40" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center py-20 lg:py-28 relative">
          <div>
            <motion.p variants={fade} initial="hidden" animate="show" className="overline text-accent mb-6">
              {cms.overline || "Client onboarding, perfected"}
            </motion.p>
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
              className="text-5xl sm:text-6xl lg:text-[4.5rem] font-light tracking-tighter leading-[0.95]">
              {cms.headline_light || "One platform to onboard"} <span className="font-black">{cms.headline_bold || "every client"}</span>{cms.headline_tail || ", in every business."}
            </motion.h1>
            <motion.p variants={fade} initial="hidden" animate="show" custom={2}
              className="mt-6 text-lg text-foreground/70 max-w-xl leading-relaxed">
              {cms.subheadline || "OnboardHQ gives any business a structured, no-code way to collect documents, signatures and approvals — with a hard-stop gate so nothing slips through."}
            </motion.p>
            <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-10 flex flex-wrap gap-4">
              <Link to="/signup" data-testid="hero-start-button"
                className="group inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground font-semibold hover:bg-accent transition-colors">
                {cms.cta_text || "Start free — 14 days"} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/pricing" data-testid="hero-pricing-button"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-primary font-semibold hover:bg-secondary transition-colors">
                View pricing
              </Link>
            </motion.div>
            <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> No card required</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Cancel anytime</span>
            </motion.div>
          </div>
          <motion.div variants={fade} initial="hidden" animate="show" custom={2} className="relative">
            <img src={HERO_IMG} alt="Structured onboarding" className="w-full h-[420px] object-cover border border-border" />
            <div className="absolute -bottom-6 -left-6 bg-card border border-border p-5 shadow-hard w-56 hidden sm:block">
              <div className="flex items-center justify-between mb-2">
                <span className="overline text-muted-foreground">Completion</span>
                <span className="text-accent font-bold">92%</span>
              </div>
              <div className="h-2 bg-muted"><div className="h-full bg-accent" style={{ width: "92%" }} /></div>
              <p className="mt-3 text-xs text-muted-foreground">Acme Lettings · 24 active clients</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {(cms.stats || [["4x", "faster onboarding"], ["100%", "compliance gate"], ["No-code", "flow builder"], ["Any", "business type"]].map(([value, label]) => ({ value, label }))).map((s, i) => (
            <div key={i} className="py-10 px-6 first:pl-0">
              <p className="text-3xl lg:text-4xl font-black tracking-tight">{s.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <p className="overline text-accent mb-3">How it works</p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">{cms.how_heading || "Launch a polished onboarding in three steps."}</h2>
        <div className="grid md:grid-cols-3 gap-px bg-border mt-12 border border-border">
          {[
            ["01", "Build your flow", "Pick a starter template or design your own stages, tasks, required docs and signatures."],
            ["02", "Invite your client", "Send a branded portal link. Your client uploads, fills and signs at their own pace."],
            ["03", "Review & go live", "Verify each submission. The hard-stop gate keeps a client pending until everything's done."],
          ].map(([n, t, d]) => (
            <div key={n} className="bg-card p-8">
              <span className="text-5xl font-black text-muted/70">{n}</span>
              <h3 className="text-xl font-medium mt-4">{t}</h3>
              <p className="text-foreground/70 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="border-y border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="overline text-accent mb-3">Built for everyone</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{cms.industries_heading || "Ready-made templates for your industry."}</h2>
              <p className="mt-4 text-foreground/70 leading-relaxed max-w-lg">
                Start from a proven flow, then tweak every stage to fit exactly how your business works.
              </p>
              <div className="grid sm:grid-cols-2 gap-px bg-border mt-8 border border-border">
                {(cms.industries || INDUSTRIES).map((ind, i) => {
                  const Icon = INDUSTRIES[i]?.icon || Building;
                  return (
                  <div key={i} className="bg-card p-6" data-testid={`industry-${i}`}>
                    <Icon className="h-6 w-6 text-accent" />
                    <h3 className="font-semibold mt-3">{ind.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{ind.desc}</p>
                  </div>
                  );
                })}
              </div>
            </div>
            <img src={TEAM_IMG} alt="Team collaboration" className="w-full h-[460px] object-cover border border-border" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <p className="overline text-accent mb-3">Everything you need</p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">{cms.features_heading || "A complete onboarding operating system."}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border mt-12 border border-border">
          {(cms.features || FEATURES).map((f, i) => {
            const Icon = FEATURES[i]?.icon || Zap;
            return (
            <div key={i} className="bg-card p-8 group hover:bg-secondary/50 transition-colors">
              <Icon className="h-7 w-7 text-accent" />
              <h3 className="text-xl font-medium mt-4">{f.title}</h3>
              <p className="text-foreground/70 mt-2 leading-relaxed">{f.desc}</p>
            </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight">{cms.cta_headline || "Ready to onboard flawlessly?"}</h2>
            <p className="mt-3 text-primary-foreground/70">{cms.cta_sub || "Set up your first flow in minutes. No card required."}</p>
          </div>
          <Link to="/signup" data-testid="cta-signup-button"
            className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-accent-foreground font-semibold hover:bg-primary-foreground hover:text-primary transition-colors whitespace-nowrap">
            Start your free trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Brand />
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} {cms.footer_note || "OnboardHQ. All rights reserved."}</p>
        </div>
      </footer>
    </div>
  );
}
